import dayjs from 'dayjs';

import { Sort, Filters } from 'modules/Documents/FiltersContext/Filters.interface';

const ALL_VALUE = 'all';

const BLACKLISTED_FILTER_KEYS = new Set([
    'page_size',
    'audit',
    'page',
    'sort',
    'isProductCategoryTabsActive',
    'isSearchActive',
    'isTabFilterActive',
    'isMemberOffboarded',
    'isEmployeeView',
    'isDocumentValidationAvailable',
    'member_uuid',
    'has_taxfyle_document_id',
]);

const DATE_FIELDS = new Set(['created_at', 'updated_at']);

const buildSortOrder = (sort: Sort) => {
    let sortOrder = [sort.by];

    if (sort.by === 'relevant_to') {
        sortOrder = ['month', 'year', 'quarter'];
    }

    return sortOrder.map((key) => ({
        [key]: {
            order: sort.order,
        },
    }));
};

export default function buildDocumentSearch(filters: Partial<Filters>) {
    const search: Record<string, any>[] = [];
    const sort = buildSortOrder(filters.sort as Sort);

    Object.entries(filters).forEach(([key, value]) => {
        // We have a set of excluded fields in the filters
        // Also we don't want to search for empty values or 'all' values
        if (
            BLACKLISTED_FILTER_KEYS.has(key) ||
            !value ||
            (Array.isArray(value) && value.length === 0) ||
            value === ALL_VALUE
        ) {
            return;
        }

        if (Array.isArray(value)) {
            // The quarters that are comming from relevantToSelect have Q in front of them
            // but the ones that are comming from the backend don't have it
            // so we need to remove to match the document in ES
            let arrayValue;
            if (value.every((item) => typeof item === 'object')) {
                arrayValue = value.map((item) => item.id);
            } else if (key === 'quarter') {
                arrayValue = value.length === 4 ? null : value.map((quarter) => parseInt(quarter.replace('Q', ''), 10));
            } else if (key === 'product_categories') {
                const [category] = value;
                arrayValue = category === ALL_VALUE ? null : value;
            } else {
                arrayValue = value;
            }

            if (arrayValue) {
                search.push({
                    terms: {
                        [key]: arrayValue,
                    },
                });
            }
        } else if (DATE_FIELDS.has(key)) {
            const range = { [key]: {} };

            switch (value) {
                case '30d':
                    range[key] = { gte: 'now-30d' };
                    break;
                case '3m':
                    range[key] = { gte: 'now-3M' };
                    break;
                case '1y':
                    range[key] = { gte: 'now-1y' };
                    break;
                default:
                    range[key] = { gte: `${value}-01-01`, lte: `${value}-12-31` };
            }
            search.push({ range });
        } else if (['boolean', 'object', 'number'].includes(typeof value)) {
            search.push({
                match: {
                    [key]: value?.id ?? value,
                },
            });
        } else {
            search.push({
                wildcard: {
                    [key]: {
                        value: `*${value}*`,
                        case_insensitive: true,
                    },
                },
            });
        }
    });

    if (filters.has_taxfyle_document_id) {
        search.push({ match: { has_taxfyle_document_id: true } });
    }

    search.sort((a, b) => {
        if ('match' in a && 'is_deleted' in a.match) {
            return -1;
        }
        if ('match' in b && 'is_deleted' in b.match) {
            return 1;
        }
        return 0;
    });

    return { search, sort };
}

type SearchRecord = Record<string, string | number | boolean>;
type FilterResult = [string, string | number | boolean] | null;

/**
 * Calculate ISO date string for relative date values.
 * Used for Django FilterSet date range filters.
 */
const getRelativeDate = (value: string): string => {
    switch (value) {
        case '30d':
            return dayjs().subtract(30, 'day').startOf('day').toISOString();
        case '3m':
            return dayjs().subtract(3, 'month').startOf('day').toISOString();
        case '1y':
            return dayjs().subtract(1, 'year').startOf('day').toISOString();
        default:
            return '';
    }
};

// Array handlers for specific filter keys
const processObjectsArray = (key: string, value: { id: string }[]): FilterResult => {
    const ids = value.map((item) => item.id).join(',');
    return ids ? [`${key}__in`, ids] : null;
};

const processQuarterArray = (value: string[]): FilterResult => {
    if (value.length === 4) {
        return null;
    }
    const quarters = value.map((q) => parseInt(q.replace('Q', ''), 10));
    return ['quarter__in', quarters.join(',')];
};

const processProductCategoriesArray = (value: string[]): FilterResult => {
    const [category] = value;
    return category === ALL_VALUE ? null : ['product_categories__in', value.join(',')];
};

const processIsInternalArray = (value: boolean[]): FilterResult => {
    const [firstValue] = value;
    return firstValue !== undefined ? ['is_internal', firstValue] : null;
};

// Unified array processor
const ARRAY_FIELDS_WITH_IN_SUFFIX = new Set(['document_type']);

const processArrayFilter = (key: string, value: unknown[]): FilterResult => {
    if (value.every((item) => typeof item === 'object' && item !== null)) {
        return processObjectsArray(key, value as { id: string }[]);
    }

    if (key === 'quarter') {
        return processQuarterArray(value as string[]);
    }
    if (key === 'product_categories') {
        return processProductCategoriesArray(value as string[]);
    }
    if (key === 'is_internal') {
        return processIsInternalArray(value as boolean[]);
    }

    if (ARRAY_FIELDS_WITH_IN_SUFFIX.has(key)) {
        return [`${key}__in`, (value as string[]).join(',')];
    }

    return [key, (value as string[]).join(',')];
};

const processDateFilter = (key: string, value: string): Array<[string, string]> => {
    const relativeDate = getRelativeDate(value);
    if (relativeDate) {
        return [[`${key}__gte`, relativeDate]];
    }
    const year = dayjs(String(value), 'YYYY');
    return [
        [`${key}__gte`, year.startOf('year').toISOString()],
        [`${key}__lte`, year.endOf('year').toISOString()],
    ];
};

const processSimpleFilter = (key: string, value: unknown): FilterResult => {
    if (typeof value === 'boolean') {
        return [key, value];
    }
    if (typeof value === 'object' && value !== null && 'id' in value) {
        return [key, (value as { id: string | number | boolean }).id];
    }
    if (typeof value === 'number') {
        return [key, value];
    }
    if (typeof value === 'string') {
        return [key, value];
    }
    return null;
};

const shouldSkipFilter = (key: string, value: unknown): boolean => {
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    return BLACKLISTED_FILTER_KEYS.has(key) || !value || isEmptyArray || value === ALL_VALUE;
};

/**
 * Build Django FilterSet compatible search parameters.
 * Transforms frontend filter state into flat key-value pairs
 * matching the MemberDocumentSearchFilter FilterSet.
 */
export function buildSearchFilters(filters: Partial<Filters>) {
    const search: SearchRecord = {};
    const sort = buildSortOrder(filters.sort as Sort);

    Object.entries(filters).forEach(([key, value]) => {
        if (shouldSkipFilter(key, value)) {
            return;
        }

        let result: FilterResult = null;

        if (Array.isArray(value)) {
            result = processArrayFilter(key, value);
        } else if (DATE_FIELDS.has(key)) {
            processDateFilter(key, value as string).forEach(([k, v]) => {
                search[k] = v;
            });
            return;
        } else {
            result = processSimpleFilter(key, value);
        }

        if (result) {
            const [resultKey, resultValue] = result;
            search[resultKey] = resultValue;
        }
    });

    if (filters.has_taxfyle_document_id) {
        search.has_taxfyle_document_id = true;
    }

    return { search, sort, parser: 'pg', member_uuid: filters.member_uuid };
}
